import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import * as actions from '~/actions';
import { logCustomEvent } from '~/api/analytics';
import { DashboardJobsType, IDashboardJobsInfo } from '~/domain/DashboardJobs';
import { EligibleJobAdapter } from '~/domain/EligibleJobs';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import { jobKeys } from '../jobKeys';
import { getConfirmCandidateOutcome, TConfirmCandidateOutcome } from './getConfirmCandidateOutcome';

interface IConfirmJobCandidate {
  id?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  profile_image?: string;
}

export interface IConfirmJobPayload {
  jobId: string;
  data?: Record<string, unknown>;
  counterOfferId?: string;
  candidate?: IConfirmJobCandidate;
  officeId?: string;
}

type TConfirmJobResponse = Record<string, any>;
type TConfirmJobError = AxiosError<{ message?: string; error?: string; title?: string }>;

interface IConfirmJobOutcomeResult {
  outcome: TConfirmCandidateOutcome;
  response: TConfirmJobResponse;
}

interface IUseConfirmJobMutationOptions {
  onOutcome?: (result: IConfirmJobOutcomeResult, variables: IConfirmJobPayload) => void;
}

/**
 * ConfirmCandidate-family mutation. This hook owns the confirm API request,
 * follow-up reads needed to classify the result, eligible-job cache seeding, and
 * cache invalidation. Callers own UI side effects through onOutcome so popup and
 * message decisions happen at the screen/call-site boundary instead of inside
 * the data mutation.
 */
export function useConfirmJobMutation(options: IUseConfirmJobMutationOptions = {}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleConfirmError = (error: TConfirmJobError) => {
    const outcome = getConfirmCandidateOutcome({
      errorStatus: error.response?.status,
      noCardErrorStatus: actions.NO_CARD_ERROR,
    });

    if (outcome.type === 'payment_missing') {
      dispatch({ type: 'CONFIRM_JOB_ACTION_STOP_LOADING' });
      dispatch({ type: actions.SHOW_PAYMENT_MISSING_POPUP });
      return;
    }

    dispatch({ type: 'CONFIRM_JOB_ACTION_FAILURE', payload: error.response?.data });
  };

  return useMutation<TConfirmJobResponse, TConfirmJobError, IConfirmJobPayload>({
    mutationFn: async ({ jobId, data, counterOfferId }) => {
      return await apiMethods[apiMethodsConst.CONFIRM_JOB_REQUEST]({
        jobId,
        data,
        counterOfferId,
      });
    },
    onMutate: () => {
      dispatch({ type: 'CONFIRM_JOB_ACTION_REQUEST' });
    },
    onSuccess: async (response, variables) => {
      try {
        const { candidate, data, jobId, officeId } = variables;

        dispatch({ type: apiMethodsConst.CONFIRM_JOB_REQUEST, payload: response });
        // Fire-and-forget: analytics must not extend the confirm spinner (GOTU-4020).
        void logCustomEvent('confirm_candidate', { ...data, jobId });

        // Read the cached pending count BEFORE invalidating below.
        const dashboardJobsInfo = queryClient.getQueryData<IDashboardJobsInfo>([
          'dashboard-jobs-info',
          DashboardJobsType.ALL,
        ]);

        let outcome = getConfirmCandidateOutcome({ response });

        if (outcome.type !== 'onsite_confirmed') {
          // Fetch both follow-up sources concurrently to shorten the confirm
          // spinner (GOTU-4020, symptom 1). The eligible-jobs result is
          // discarded when the "another job" popup wins — one speculative GET
          // is cheaper than chaining the two requests serially.
          const [pendingJobs, eligibleJobsResponse] = await Promise.all([
            apiMethods.getJobsRequest({
              limit: 15,
              page: 1,
              professionalId: candidate?.id,
              status: 'pending-counter',
              sortField: 'job_datetime',
              sortOrder: 'asc',
              officeId,
            }),
            apiMethods.getEligibleJobsRequest({
              professionalId: candidate?.id,
              page: 1,
              limit: 10,
              status: undefined,
              xOfficeId: officeId,
              profession: response?.profession,
            }),
          ]);

          const hasEligibleJobs = eligibleJobsResponse?.response?.length > 0;
          outcome = getConfirmCandidateOutcome({
            candidateId: candidate?.id,
            dashboardPendingCount: dashboardJobsInfo?.pendingCount,
            eligibleJobsCount: eligibleJobsResponse?.response?.length ?? 0,
            jobId,
            pendingJobs,
            response,
          });

          if (outcome.type === 'show_eligible_jobs_popup' && hasEligibleJobs) {
            const transformedResult =
              EligibleJobAdapter.toGetEligibleJobsResult(eligibleJobsResponse);
            const queryKey = [
              'eligible-jobs-infinite',
              {
                professionalId: candidate?.id,
                limit: 10,
                enabled: true,
                profession: response?.profession,
              },
            ];

            queryClient.setQueryData(queryKey, {
              pages: [transformedResult],
              pageParams: [1],
            });
          }
        }

        // Callers dispatch popup/message UI before job detail invalidation can
        // refetch this job as SCHEDULED and re-enter the cold-entry redirect path.
        options.onOutcome?.({ outcome, response }, variables);

        queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-jobs-info'] });
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });

        dispatch({ type: 'CONFIRM_JOB_ACTION_SUCCESS' });
      } catch (error) {
        handleConfirmError(error as TConfirmJobError);
      }
    },
    onError: handleConfirmError,
  });
}
